/**
 * Prescription Tools Layout
 *
 * Layout component for the /prescription-tools page.
 * Provides tab navigation between Selection Tool and Full List views.
 *
 * Part of OC2-3562 Text Prescriptions feature.
 */

import NonFullWidthWrapper from '@/components/NonFullWidthWrapper'
import PrescriptionFullList from '@/components/prescriptions/PrescriptionFullList'
import PrescriptionSelectionTool from '@/components/prescriptions/PrescriptionSelectionTool'
import RichText from '@/components/RichText'
import { CommonCopyUrlWrapper } from '@/components/ui/copy-to-clipboard/CommonCopyUrlWrapper'
import { useCleanPathDefault } from '@/hooks/useCleanPathDefault'
import type { PrescriptionsList } from '@/kontent/content-types/prescriptions_list'
import type { WebPagePrescriptionsTools } from '@/kontent/content-types/web_page_prescriptions_tools'
import TabBar from '@/legacy-ported/components/tabs/TabBar'
import setTabNavigation from '@/legacy-ported/utilities/hooks/useTabNavigation'
import type { CommonPageProps, Mapping } from '@/types'
import { isRichtextElementEmpty } from '@/utils'
import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { useRouter } from 'next/router'
import { Alert, FormGroupSelect } from 'nsw-ds-react'
import type { FormOption } from 'nsw-ds-react/dist/component/forms'
import { useEffect, useMemo, useState, type ChangeEvent } from 'react'

// Tab types
type TabViewOption = 'selection' | 'list'

const PRESCRIPTION_TABS = [
	{ id: 'selection', name: 'Selection tool', index: 0 },
	{ id: 'list', name: 'Alphabetical list', index: 1 },
]

const LIST_QUERY_PARAM = 'list'
const PLACEHOLDER_VALUE = ''

const buildListLabel = (list: PrescriptionsList): string => {
	const title = list.elements.title.value
	const years = list.elements.years.value
	return years ? `${title} (${years})` : title
}

export interface WpPrescriptionToolsLayoutProps
	extends CommonPageProps<any, any> {
	page: WebPagePrescriptionsTools
	pageLinkedItems: IContentItemsContainer
	prescriptionLists: PrescriptionsList[]
	prescriptionListsLinkedItems: IContentItemsContainer
}

interface PrescriptionListStatusProps {
	list: PrescriptionsList
	mappings: Mapping[]
	linkedItems: IContentItemsContainer
}

function PrescriptionListStatus({
	list,
	mappings,
	linkedItems,
}: PrescriptionListStatusProps) {
	if (isRichtextElementEmpty(list.elements.status_message)) {
		return null
	}
	return (
		<div className="mb-6">
			<Alert
				as="warning"
				title=""
				data-testid="prescription-tools-status-alert"
			>
				<RichText
					richTextElement={list.elements.status_message}
					linkedItems={linkedItems}
					mappings={mappings}
				/>
			</Alert>
		</div>
	)
}

function WpPrescriptionToolsLayout(props: WpPrescriptionToolsLayoutProps) {
	const {
		page,
		pageLinkedItems,
		mappings,
		prescriptionLists,
		prescriptionListsLinkedItems,
	} = props
	const router = useRouter()
	const currentUrl = useCleanPathDefault()

	const pageTitle = page.elements.title.value
	const pageContent = page.elements.web_content_rtb__content

	const hasAnyActiveList = prescriptionLists.length > 0
	const isSingleList = prescriptionLists.length === 1

	const initialListCodename = useMemo(() => {
		const queryValue = router.query[LIST_QUERY_PARAM]
		const codename = Array.isArray(queryValue) ? queryValue[0] : queryValue
		if (
			codename &&
			prescriptionLists.some((l) => l.system.codename === codename)
		) {
			return codename
		}
		if (isSingleList) {
			return prescriptionLists[0].system.codename
		}
		return PLACEHOLDER_VALUE
	}, [prescriptionLists, router.query, isSingleList])

	const [selectedListCodename, setSelectedListCodename] =
		useState<string>(initialListCodename)

	useEffect(() => {
		const queryValue = router.query[LIST_QUERY_PARAM]
		const codename = Array.isArray(queryValue) ? queryValue[0] : queryValue
		if (
			codename &&
			prescriptionLists.some((l) => l.system.codename === codename) &&
			codename !== selectedListCodename
		) {
			setSelectedListCodename(codename)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.query, prescriptionLists])

	useEffect(() => {
		if (!router.isReady) return
		if (!isSingleList) return
		const queryValue = router.query[LIST_QUERY_PARAM]
		const codename = Array.isArray(queryValue) ? queryValue[0] : queryValue
		if (codename === selectedListCodename) return
		if (!selectedListCodename) return
		router.replace(
			{
				pathname: router.pathname,
				query: {
					...router.query,
					[LIST_QUERY_PARAM]: selectedListCodename,
				},
			},
			undefined,
			{ shallow: true },
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSingleList, selectedListCodename, router.isReady])

	const selectedList =
		prescriptionLists.find(
			(l) => l.system.codename === selectedListCodename,
		) ?? null

	const [selectedTab, setSelectedTab] = useState<TabViewOption>('selection')

	useEffect(() => {
		const tabParam = router.query.tab as string
		if (tabParam === 'selection' || tabParam === 'list') {
			setSelectedTab(tabParam)
		}
	}, [router.query.tab])

	const updateTabAndUrl = (tabView: TabViewOption) => {
		setSelectedTab(tabView)
		router.replace(
			{
				pathname: router.pathname,
				query: { ...router.query, tab: tabView },
			},
			undefined,
			{ shallow: true },
		)
	}

	const handleTabChange = (tabKey: string) => {
		updateTabAndUrl(tabKey as TabViewOption)
	}

	const handleTabPrevious = () => {
		const newTab = setTabNavigation(
			PRESCRIPTION_TABS,
			selectedTab,
			'PREVIOUS',
		)
		if (newTab) {
			updateTabAndUrl(newTab.id as TabViewOption)
		}
	}

	const handleTabNext = () => {
		const newTab = setTabNavigation(PRESCRIPTION_TABS, selectedTab, 'NEXT')
		if (newTab) {
			updateTabAndUrl(newTab.id as TabViewOption)
		}
	}

	const handleListChange = (e: ChangeEvent<HTMLSelectElement>) => {
		const newCodename = e.target.value
		setSelectedListCodename(newCodename)
		const nextQuery = { ...router.query }
		if (newCodename) {
			nextQuery[LIST_QUERY_PARAM] = newCodename
		} else {
			delete nextQuery[LIST_QUERY_PARAM]
		}
		router.replace(
			{ pathname: router.pathname, query: nextQuery },
			undefined,
			{ shallow: true },
		)
	}

	const dropdownOptions: FormOption[] = [
		{
			text: 'Select Prescriptions list',
			value: PLACEHOLDER_VALUE,
		},
		...prescriptionLists.map((list) => ({
			text: buildListLabel(list),
			value: list.system.codename,
		})),
	]

	return (
		<>
			{pageTitle && (
				<CommonCopyUrlWrapper url={currentUrl} className="mb-8">
					<h1
						data-kontent-item-id={page.system.id}
						data-kontent-element-codename="title"
					>
						{pageTitle}
					</h1>
				</CommonCopyUrlWrapper>
			)}

			<NonFullWidthWrapper>
				<div className="mb-8 text-nsw-grey-01">
					<RichText
						richTextElement={pageContent}
						mappings={mappings}
						linkedItems={pageLinkedItems}
					/>
				</div>
			</NonFullWidthWrapper>

			{hasAnyActiveList && (
				<div
					className="mb-8 max-w-md"
					data-testid="prescription-list-selector"
				>
					{isSingleList ? (
						<div
							className="nsw-h4 text-[18px]"
							data-testid="prescription-list-fixed"
						>
							{buildListLabel(prescriptionLists[0])}
						</div>
					) : (
						<FormGroupSelect
							value={selectedListCodename}
							label="Select Prescriptions list"
							hideLabel
							name="prescriptionList"
							placeholder=""
							options={dropdownOptions}
							onChange={handleListChange}
							data-testid="prescription-list-dropdown"
						/>
					)}
				</div>
			)}

			{selectedList && (
				<div
					className="mt-8"
					css={{
						'.MuiTabs-indicator': {
							backgroundColor: 'var(--nsw-grey-01)',
						},
					}}
				>
					<TabBar
						value={selectedTab}
						onChange={handleTabChange}
						tabs={PRESCRIPTION_TABS.map((tab) => ({
							tabId: tab.id,
							label: tab.name,
							panelId: `tab-panel-${tab.id}`,
							className:
								tab.id === selectedTab
									? 'syllabus-header__tab--selected'
									: 'syllabus-header__tab',
						}))}
						className="w-full bg-white syllabus-header__tab-bar"
						onPreviousClick={handleTabPrevious}
						onNextClick={handleTabNext}
					/>

					<div className="p-4 pt-12">
						{selectedTab === 'selection' && (
							<div
								id="tab-panel-selection"
								role="tabpanel"
								aria-labelledby="selection"
							>
								<PrescriptionListStatus
									list={selectedList}
									mappings={mappings}
									linkedItems={prescriptionListsLinkedItems}
								/>
								<PrescriptionSelectionTool
									tabContent={
										page.elements.content_selection_tab
									}
									mappings={mappings}
									linkedItems={{}}
									listcodename={selectedList.system.codename}
								/>
							</div>
						)}

						{selectedTab === 'list' && (
							<div
								id="tab-panel-list"
								role="tabpanel"
								aria-labelledby="list"
							>
								<PrescriptionListStatus
									list={selectedList}
									mappings={mappings}
									linkedItems={prescriptionListsLinkedItems}
								/>
								<PrescriptionFullList
									tabContent={page.elements.content_list_tab}
									mappings={mappings}
									linkedItems={{}}
									listcodename={selectedList.system.codename}
								/>
							</div>
						)}
					</div>
				</div>
			)}
		</>
	)
}

export default WpPrescriptionToolsLayout
